import { useEffect, useMemo, useState } from "react"
import { attributeMapping } from "../../utils/programmaticUtils"
import { ItemSummary, LayerItemAttribute } from "../../services/types"
import { formatValue, parseMarkdown } from "../../utils/helpers"

// Section Images
import waterSvg from '../../assets/programs/Water.svg';
import transportationSvg from '../../assets/programs/Transportation.svg';
import sanitarySvg from '../../assets/programs/Sanitary Sewer.svg';
import powerSvg from '../../assets/programs/Power Network.svg';
import generalSvg from '../../assets/programs/General.svg';
import fundamentalsSvg from '../../assets/programs/Fundamentals.svg';
import broadbandSvg from '../../assets/programs/Broadband.svg';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { fetchProgramDetailDashboardView } from "../../utils/dashboardUtils";
import { useOrgContext } from "../../context/OrgIdContext";


interface Attribute {
  name: string
  value: string
}

interface MapItemMatrixUIProps {
  name: string;
  description?: string;
  category?: string;
  value?: number;
  lat?: number;
  long?: number;
  attributes: LayerItemAttribute[];
  summary: ItemSummary | undefined;
  onClose?: () => void;
}

interface CollapsibleSectionProps {
  title: string;
  attributes: Attribute[];
  isExpanded: boolean;
  onToggle: () => void;
}

const getCategoryColor = (category?: string) => {
    if (!category) return "bg-gray-600 text-white"; 

    switch (category?.toLowerCase()) {
        case "near term":
            return "bg-[#A3C9A8] text-[#1B4332]";
        case "mid term":
            return "bg-[#A0BFE0] text-[#102A43]";
        case "near term - unlikely to convert":
            return "bg-[#FFE29A] text-[#5F370E]";
        case "long term":
            return "bg-[#CA9DFF] text-[#49276F]";
        default:
            return "bg-gray-600 text-white";
    };
};

function CollapsibleSection({ title, attributes, isExpanded, onToggle }: CollapsibleSectionProps) {
    const scoreMap: Record<string, { label: string; max: number }> = {
        "Fundamentals": { label: "Fundamentals Score", max: 53 },
        "Transportation": { label: "Transportation Score", max: 24 },
        "Power Network": { label: "Power Network Score", max: 6 },
        "Broadband": { label: "Broadband Score", max: 5 },
        "Water": { label: "Water Score", max: 6 },
        "Sanitary Sewer": { label: "Sanitary Sewer Score", max: 6 },
        "General": { label: "", max: 0 } // no score
    };

    const scoreValue = useMemo(() => {
        const config = scoreMap[title];
        
        if (!config.label) return null; // No score for General
        const scoreAttr = attributes.find(attr => attr.name === config.label);
        return scoreAttr ? `${scoreAttr.value} out of ${config.max}` : null;
    }, [title, attributes]);

    const svgMap: Record<string, string> = {
        General: generalSvg,
        Fundamentals: fundamentalsSvg,
        Transportation: transportationSvg,
        "Power Network": powerSvg,
        Broadband: broadbandSvg,
        Water: waterSvg,
        "Sanitary Sewer": sanitarySvg,
    }

    return (
        <div className="border-b border-gray-300">
        <button
            onClick={onToggle}
            className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-start space-x-2">
                    <img src={svgMap[title]} className="w-5 h-5"></img>
                    <div className="flex flex-col text-left">
                        <span className="font-semibold text-sm uppercase tracking-wide">{title}</span>
                        {scoreValue && (
                            <span className="text-xs">
                                Score: {scoreValue}
                            </span>
                        )}
                    </div>
                </div>
                <span>{isExpanded ? "−" : "+"}</span>
            </div>
        </button>

        {isExpanded && (
            <div className="bg-white">
            {attributes
                .filter(attr => !attr.name.toLowerCase().includes("score"))
                .map((attr, index) => (
                <div key={index} className="grid grid-cols-2 border-b border-gray-200 last:border-b-0">
                <div className="p-3 bg-gray-50 border-r border-gray-200">
                    <span className="text-xs font-medium">{attr.name}</span>
                </div>
                <div className="p-3">
                    <span className='text-xs' dangerouslySetInnerHTML={{ __html: parseMarkdown(attr.value) }}></span>
                </div>
                </div>
            ))}
            </div>  
        )}
        </div>  
    )
}

export default function MapItemMatrixUI({
  name,
  description,
  category,
  value,
  lat,
  long,
  attributes,
  summary,
}: MapItemMatrixUIProps) {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        General: false,
        Core: false,
        Transportation: false,
        "Power Network": false,
        Broadband: false,
        Water: false,
        "Sanitary Sewer": false,
    });
    const [streetViewLoaded, setStreetViewLoaded] = useState(false);
    const [streetViewSrc, setStreetViewSrc] = useState<string | null>(null);
    // State for view mode to update display of map item matrix UI on 'bottom' view
    const { locationId } = useOrgContext();
    const [viewMode, setViewMode] = useState<string | null>(null);
    const [showStreetView, setShowStreetView] = useState(false);

    useEffect(() => {
        const fetchView = async () => {
            const mode = await fetchProgramDetailDashboardView(locationId);
            setViewMode(mode);
        };

        fetchView();
    }, [locationId]);

    useEffect(() => {
        if (lat && long) {
            const url = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getstreetview?lat=${lat}&lng=${long}`;
            const img = new Image();
            img.src = url;
            img.onload = () => {
            setStreetViewSrc(url);
            setStreetViewLoaded(true);
            };
        }
    }, [lat, long]);

    const toggleStreetView = () => setShowStreetView(prev => !prev);

    const toggleSection = (sectionName: string) => {
        setExpandedSections((prev) => ({
        ...prev,
        [sectionName]: !prev[sectionName],
        }))
    }
    console.log('attributes:', attributes);

    // Create attribute grouping based on attribute map, excluding score attributes
    const groupedAttributes = Object.entries(attributeMapping)
    .map(([sectionName, attributeNames]) => {
        const sectionAttributes =
        attributeNames.includes('*')
            ? attributes
                .filter(attr => 
                attr.value && !attr.name.toLowerCase().includes('score')
                )
                .map(attr => ({
                name: attr.name,
                value: attr.value
                }))
            : attributeNames
                .map(attrName => {
                    const attr = attributes.find(a => a.name === attrName);
                    if (!attr || attr.name.toLowerCase().includes('score') || !attr.value) return null;
                    return { name: attr.name, value: attr.value };
                    })
                    .filter((attr): attr is Attribute => !!attr);

        return {
        sectionName,
        attributes: sectionAttributes
        };
    })
    .filter(section => section.attributes.length > 0);

    // const overallScoreAttr = attributes.find(attr => attr.name === "Overall Score");

    return (
        <div className={`mx-auto bg-white border-0 border-b-2 border-gray-300 shadow-lg ${viewMode === 'bottom' ? 'w-full max-w-none h-full overflow-auto' : 'max-w-2xl'}`}>
            <div className={`flex flex-col items-start p-4 border-b ${getCategoryColor(category)}`}>
                <h1 className="text-xl font-bold uppercase tracking-wide">{name}</h1>
                {viewMode === 'bottom' && streetViewLoaded && (
                    <button
                        onClick={toggleStreetView}
                        className="text-xs font-medium text-white hover:underline"
                    >
                        {showStreetView ? "Hide Site Image" : "Show Site Image"}
                    </button>
                )}
            </div>
            
            {viewMode !== 'bottom' && streetViewSrc && streetViewLoaded ? (
                <img
                    src={streetViewSrc}
                    alt="Site map"
                    style={{
                    width: '100%',
                    maxHeight: '20rem',
                    }}
                />
                ) : viewMode !== 'bottom' ? (
                <Skeleton height={200} />
                ) : null}

            {description && (
                <div className="p-4 border-b border-gray-300">
                    <p className='text-xs' dangerouslySetInnerHTML={{ __html: parseMarkdown(description) }}></p>
                </div>
            )}

            {category && (
                <div className="grid grid-cols-2 border-b border-gray-300">
                    <div className="p-3 flex items-center border-r border-gray-300 font-semibold text-xs">Category</div>
                    <div className="p-3 text-xs">{category}</div>
                </div>
            )}

            {value !== undefined && (
                <div className="grid grid-cols-2 border-b border-gray-300">
                    <div className="p-3 border-r border-gray-300 font-semibold text-xs">Value:</div>
                    <div className="p-3 text-xs">
                        {formatValue(value, summary?.unit ?? 'integer')} <span className="text-[10px]">{summary?.unit_descriptor || ''}</span>
                    </div>
                </div>
            )}

            {/* <div className="grid grid-cols-2 border-b border-gray-300">
                <div className="p-3 border-r border-gray-300 font-semibold text-xs">Overall Score:</div>
                <div className="p-3 text-xs">
                    {overallScoreAttr?.value ?? "—"} <span className="text-[10px]">out of 100</span>
                </div>
            </div> */}

            <div>
                {groupedAttributes.map(({ sectionName, attributes }) => (
                <CollapsibleSection
                    key={sectionName}
                    title={sectionName}
                    attributes={attributes}
                    isExpanded={expandedSections[sectionName]}
                    onToggle={() => toggleSection(sectionName)}
                />
                ))}
            </div>
            {viewMode === 'bottom' && showStreetView && streetViewSrc && (
                <div className="border-b border-gray-200 bg-gray-50 p-4 flex justify-center">
                    <div className="rounded-lg shadow-md border border-gray-300 overflow-hidden w-[80vw]">
                    <img
                        src={streetViewSrc}
                        alt="Site map"
                        className="w-full object-cover"
                    />
                    </div>
                </div>
            )}
        </div>
    )
}
